package org.zq.astrock_ai.quant

import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sqrt

/**
 * 建议买入区间（研究参考）。
 * 多锚点汇合：结构摆动低点 + 量能承接 + 均线支撑 + 布林下轨/ATR 回撤 +（可选）回测入场均价。
 */
data class BuyZonePlan(
    val low: Double,
    val high: Double,
    val confidence: Double,
    val method: String,
    val reasons: List<String>,
    val anchors: List<Pair<String, Double>> = emptyList(),
)

/**
 * @param signal BUY/SELL/HOLD
 * @param backtestEntryAvg 最近回测买入均价（可选）
 * @param market 主力资金 + 公告标题（可选，微调区间）
 */
fun buildBuyZonePlan(
    bars: List<QuantBar>,
    price: Double = 0.0,
    signal: String = "HOLD",
    backtestEntryAvg: Double? = null,
    market: ZoneMarketContext? = null,
): BuyZonePlan {
    val px = when {
        price > 0 -> price
        else -> bars.lastOrNull()?.close ?: 0.0
    }
    if (px <= 0 || bars.size < 10) {
        return BuyZonePlan(0.0, 0.0, 0.0, "数据不足", emptyList())
    }

    val recent20 = bars.takeLast(20)
    val recent60 = bars.takeLast(60)
    val recent120 = bars.takeLast(120)
    val r20Low = recent20.minOf { it.low }
    val atr = atr14(bars).coerceAtLeast(px * 0.008)
    val bollLower = bollLower20(bars)
    val atrPull1 = px - atr * 1.0
    val atrPull2 = px - atr * 1.8
    val ma20 = sma(bars, 20)
    val ma60 = sma(bars, 60)

    val swingLows = findSwingLows(recent120, left = 2, right = 2)
        .filter { it <= px * 1.005 }
        .sorted()
        .distinctBy { roundPrice(it) }
        .take(3)
    val volumeNode = lowVolumeNode(recent60, px)
    val primarySwing = swingLows.firstOrNull() ?: recent60.dropLast(3).minOfOrNull { it.low } ?: r20Low

    val anchors = buildList {
        add("近20日低点" to r20Low)
        add("近端结构低点" to primarySwing)
        swingLows.drop(1).forEachIndexed { i, p ->
            add("历史摆动低${i + 2}" to p)
        }
        if (bollLower > 0) add("布林下轨" to bollLower)
        volumeNode?.let { add("高量承接节点" to it) }
        if (ma20 in (px * 0.85)..(px * 1.005)) add("MA20支撑" to ma20)
        if (ma60 in (px * 0.80)..(px * 1.005)) add("MA60支撑" to ma60)
        add("ATR1.0回撤" to atrPull1)
        add("ATR1.8回撤" to atrPull2)
        backtestEntryAvg?.takeIf { it > px * 0.75 && it < px * 1.08 }?.let {
            add("回测入场均价" to it)
        }
    }

    val downside = anchors.map { it.second }.filter { it > 0 && it <= px * 1.005 }
    if (downside.isEmpty()) {
        return BuyZonePlan(0.0, 0.0, 0.0, "无下方支撑锚点", emptyList())
    }

    val cluster = clusterAroundMedian(downside)
    val median = cluster.sorted()[cluster.size / 2]
    var bandHigh = min(px * 0.998, cluster.maxOrNull() ?: median)
    var bandLow = min(bandHigh * 0.992, cluster.minOrNull() ?: (median * 0.985))
    bandLow = bandLow.coerceAtLeast(px * 0.85)

    val bounceCount = countSupportBounces(bars, primarySwing)
    if (bounceCount >= 2) {
        val tightHigh = min(px * 0.999, primarySwing * 1.015)
        val tightLow = min(tightHigh * 0.992, primarySwing * 0.985)
        bandHigh = min(bandHigh, tightHigh)
        bandLow = max(bandLow * 0.5 + tightLow * 0.5, tightLow)
    }

    val side = signal.uppercase()
    val tech = when (side) {
        "BUY" -> roundPrice(max(bandLow, atrPull2.coerceAtLeast(px * 0.88))) to
            roundPrice(min(px * 0.999, max(bandHigh, primarySwing * 1.01)))
        "SELL" -> roundPrice(bandLow) to roundPrice(min(bandHigh, px * 0.99))
        else -> roundPrice(bandLow) to roundPrice(bandHigh)
    }
    val marketAdj = evaluateZoneMarketAdjust(market)
    val (low, high) = applyBuyMarketAdjust(tech.first, tech.second, px, marketAdj)
    if (low <= 0 || high <= 0 || high < low) {
        return BuyZonePlan(0.0, 0.0, 0.0, "区间无效", emptyList())
    }

    val nearCount = anchors.count { abs(it.second - median) / median <= 0.02 }
    val confidence = (
        0.32 +
            nearCount * 0.10 +
            (if (bounceCount >= 2) 0.10 else 0.0) +
            (if (volumeNode != null) 0.06 else 0.0) +
            (if (backtestEntryAvg != null) 0.08 else 0.0) +
            (if (swingLows.isNotEmpty()) 0.06 else 0.0) +
            marketAdj.confidenceDelta
        ).coerceIn(0.30, 0.93)

    val method = when {
        marketAdj.riskScore >= 0.35 -> "历史支撑+公告风险折价"
        marketAdj.flowScore >= 0.45 -> "历史支撑+资金抬升"
        marketAdj.flowScore <= -0.45 -> "历史支撑+资金走弱"
        nearCount >= 3 && bounceCount >= 2 -> "历史支撑+反弹确认汇合"
        nearCount >= 3 -> "多锚点历史支撑带"
        volumeNode != null && swingLows.isNotEmpty() -> "结构低点+量能承接"
        side == "BUY" -> "偏多回撤吸纳带"
        else -> "历史支撑+波动回撤"
    }

    val reasons = buildList {
        add("汇合区间 ${roundPrice(low)}–${roundPrice(high)}（置信 ${(confidence * 100).toInt()}%）")
        if (tech.first != low || tech.second != high) {
            add("技术锚点原区间 ${roundPrice(tech.first)}–${roundPrice(tech.second)}，已按资金/公告微调")
        }
        add(
            "近20日低点 ${roundPrice(r20Low)}；近端结构低点 ${roundPrice(primarySwing)}" +
                if (bounceCount > 0) "（近60日 $bounceCount 次触底反弹）" else "",
        )
        if (bollLower > 0) {
            add("布林下轨 ${roundPrice(bollLower)}；ATR14=${roundPrice(atr)}（约 ${round1(atr / px * 100)}%）")
        }
        volumeNode?.let {
            add("近60日高量承接节点约 ${roundPrice(it)}，历史上该价带买盘较活跃")
        }
        val maParts = buildList {
            if (ma20 in (px * 0.85)..(px * 1.005)) add("MA20=${roundPrice(ma20)}")
            if (ma60 in (px * 0.80)..(px * 1.005)) add("MA60=${roundPrice(ma60)}")
        }
        if (maParts.isNotEmpty()) {
            add("下方均线支撑：${maParts.joinToString("、")}")
        }
        addAll(marketAdj.reasons)
        when (side) {
            "BUY" -> add("当前偏多：买区放在下方支撑/回撤带，避免追高")
            "SELL" -> add("当前偏空：若计划低吸，需更靠近支撑且严格止损")
            else -> add("信号中性：以支撑带观察企稳后再考虑分批")
        }
        backtestEntryAvg?.let {
            add("策略回测近期入场均价约 ${roundPrice(it)}，与历史支撑交叉验证")
        }
        add("说明：技术吸纳带而非必买价；不构成收益保证")
    }

    return BuyZonePlan(
        low = low,
        high = high,
        confidence = (confidence * 100).toInt() / 100.0,
        method = method,
        reasons = reasons,
        anchors = anchors.map { it.first to roundPrice(it.second) },
    )
}

private fun findSwingLows(bars: List<QuantBar>, left: Int, right: Int): List<Double> {
    if (bars.size < left + right + 1) return emptyList()
    val out = ArrayList<Double>()
    for (i in left until bars.size - right) {
        val l = bars[i].low
        var isTrough = true
        for (j in i - left until i) {
            if (bars[j].low <= l) {
                isTrough = false
                break
            }
        }
        if (!isTrough) continue
        for (j in i + 1..i + right) {
            if (bars[j].low < l) {
                isTrough = false
                break
            }
        }
        if (isTrough) out += l
    }
    return out
}

private fun lowVolumeNode(bars: List<QuantBar>, price: Double): Double? {
    if (bars.size < 15 || price <= 0) return null
    val downside = bars.filter { it.low <= price * 1.01 && it.volume > 0 }
    if (downside.size < 5) return null
    val lo = downside.minOf { it.low }
    val hi = downside.maxOf { it.high }.coerceAtLeast(price)
    if (hi <= lo) return null
    val buckets = 12
    val width = (hi - lo) / buckets
    if (width <= 0) return null
    val vol = DoubleArray(buckets)
    downside.forEach { bar ->
        val mid = (bar.high + bar.low) / 2.0
        val idx = ((mid - lo) / width).toInt().coerceIn(0, buckets - 1)
        vol[idx] += bar.volume
    }
    val best = vol.indices.maxByOrNull { vol[it] } ?: return null
    if (vol[best] <= 0) return null
    val node = lo + (best + 0.5) * width
    return node.takeIf { it <= price * 1.005 && it > 0 }
}

private fun countSupportBounces(bars: List<QuantBar>, support: Double): Int {
    if (support <= 0 || bars.size < 8) return 0
    val bandHigh = support * 1.015
    var count = 0
    bars.takeLast(60).windowed(2).forEach { (a, b) ->
        val touched = a.low <= bandHigh || b.low <= bandHigh
        val bounced = b.close > a.close && b.close > support * 1.005
        if (touched && bounced) count++
    }
    return count.coerceAtMost(8)
}

private fun sma(bars: List<QuantBar>, n: Int): Double {
    if (bars.size < n) return 0.0
    return bars.takeLast(n).map { it.close }.average()
}

private fun clusterAroundMedian(values: List<Double>): List<Double> {
    if (values.isEmpty()) return emptyList()
    val sorted = values.sorted()
    val mid = sorted[sorted.size / 2]
    val tight = sorted.filter { abs(it - mid) / mid <= 0.035 }
    return if (tight.size >= 2) tight else sorted
}

private fun atr14(bars: List<QuantBar>): Double {
    if (bars.size < 2) return 0.0
    val trs = ArrayList<Double>(bars.size)
    for (i in 1 until bars.size) {
        val cur = bars[i]
        val prev = bars[i - 1]
        val tr = max(cur.high - cur.low, max(abs(cur.high - prev.close), abs(cur.low - prev.close)))
        trs += tr
    }
    return trs.takeLast(14).average()
}

private fun bollLower20(bars: List<QuantBar>): Double {
    val closes = bars.map { it.close }.takeLast(20)
    if (closes.size < 10) return 0.0
    val ma = closes.average()
    val variance = closes.sumOf { (it - ma) * (it - ma) } / closes.size
    return ma - 2.0 * sqrt(variance)
}

private fun roundPrice(v: Double): Double =
    (v * 100.0).toInt() / 100.0

private fun round1(v: Double): String {
    val x = (v * 10.0).toInt() / 10.0
    return x.toString()
}
